
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Appointment, Doctor } from '@/types';
import { format } from 'date-fns';
import { useToast } from "@/hooks/use-toast";
import { doctorService } from '@/services/doctorService';
import { appointmentService } from '@/services/appointmentService';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { shouldUseMockData } from '@/services/api';
import { toast } from '@/components/ui/sonner';

const AppointmentManagement: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState<Appointment | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'confirmed' | 'cancelled' | 'completed'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const { toast: hookToast } = useToast();

  // Load doctors data
  const loadDoctors = async () => {
    try {
      const data = await doctorService.getAll();
      setDoctors(data);
      return true;
    } catch (error) {
      console.error('Error loading doctors:', error);
      // Silent failure - we'll use appointments data to determine overall status
      if (shouldUseMockData()) {
        // Import mock data as fallback
        const { doctors: mockDocs } = await import('@/data/mockData');
        setDoctors(mockDocs);
      }
      return false;
    }
  };
  
  // Load appointments data
  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      setIsRetrying(true);
      
      const doctorsSuccess = await loadDoctors();
      
      const data = await appointmentService.getAll();
      console.log('Fetched appointments:', data);
      setAppointments(data);
      
      setIsRetrying(false);
      
      if (!doctorsSuccess) {
        toast.warning("Using cached doctor data", {
          description: "Could not refresh doctor information"
        });
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      setHasError(true);
      setIsRetrying(false);
      
      // Attempt to load mock data if available
      if (shouldUseMockData()) {
        try {
          const { appointments: mockApps } = await import('@/data/mockData');
          setAppointments(mockApps);
          toast.warning("Using local appointment data", {
            description: "Could not connect to server. Some features may be limited."
          });
          setHasError(false);
        } catch (e) {
          hookToast({
            title: "Error loading appointments",
            description: "Failed to load even local appointment data. Please refresh the page.",
            variant: "destructive",
          });
        }
      } else {
        hookToast({
          title: "Error loading appointments",
          description: "Failed to load appointments data. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadAppointments();
    
    // Refresh data periodically
    const interval = setInterval(() => {
      if (!isRetrying) {
        loadAppointments();
      }
    }, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, []);
  
  const handleStatusChange = async (newStatus: 'confirmed' | 'cancelled' | 'completed') => {
    if (!currentAppointment) return;
    
    try {
      // Update appointment status in backend
      const updatedAppointment = await appointmentService.update(currentAppointment.id, {
        ...currentAppointment,
        status: newStatus
      });
      
      // Update local state
      const updatedAppointments = appointments.map(appointment => {
        if (appointment.id === currentAppointment.id) {
          return updatedAppointment;
        }
        return appointment;
      });
      
      setAppointments(updatedAppointments);
      setIsStatusDialogOpen(false);
      
      const statusMessages = {
        confirmed: "Appointment has been confirmed.",
        cancelled: "Appointment has been cancelled.",
        completed: "Appointment has been marked as completed."
      };
      
      toast(statusMessages[newStatus], {
        description: `Status updated for ${currentAppointment.patientName}`,
      });
    } catch (error) {
      console.error('Error updating appointment status:', error);
      hookToast({
        title: "Update failed",
        description: "Failed to update appointment status. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const openStatusDialog = (appointment: Appointment) => {
    setCurrentAppointment(appointment);
    setIsStatusDialogOpen(true);
  };
  
  const openDetailsDialog = (appointment: Appointment) => {
    setCurrentAppointment(appointment);
    setIsDetailsDialogOpen(true);
  };
  
  const getDoctorName = (doctorId: number) => {
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? doctor.name : 'Unknown Doctor';
  };
  
  const filteredAppointments = statusFilter === 'all' 
    ? appointments 
    : appointments.filter(appointment => appointment.status === statusFilter);
  
  const statusColorMap = {
    'confirmed': 'bg-green-100 text-green-800',
    'cancelled': 'bg-red-100 text-red-800',
    'completed': 'bg-blue-100 text-blue-800'
  };
  
  return (
    <div className="container mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className="text-2xl font-bold">Appointment Management</h1>
        <div className="flex gap-2">
          <Select 
            value={statusFilter} 
            onValueChange={(value: 'all' | 'confirmed' | 'cancelled' | 'completed') => setStatusFilter(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Appointments</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadAppointments}
            disabled={isRetrying}
            className="flex gap-1 items-center"
          >
            {isRetrying ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Refresh
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Summary Cards */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">Total Appointments</span>
              <span className="text-3xl font-bold">{appointments.length}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">Confirmed</span>
              <span className="text-3xl font-bold text-green-600">
                {appointments.filter(a => a.status === 'confirmed').length}
              </span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">Cancelled</span>
              <span className="text-3xl font-bold text-red-500">
                {appointments.filter(a => a.status === 'cancelled').length}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading && appointments.length === 0 ? (
          <div className="flex flex-col justify-center items-center p-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-muted-foreground">Loading appointments...</span>
          </div>
        ) : hasError ? (
          <div className="p-6">
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4 mr-2" />
              <AlertDescription className="flex justify-between items-center w-full">
                <span>Error loading appointments. Please try again.</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadAppointments}
                  disabled={isRetrying}
                  className="ml-2"
                >
                  {isRetrying ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient</TableHead>
                <TableHead>Doctor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No appointments found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAppointments.map(appointment => (
                  <TableRow key={appointment.id}>
                    <TableCell className="font-medium">{appointment.patientName}</TableCell>
                    <TableCell>{getDoctorName(appointment.doctorId)}</TableCell>
                    <TableCell>
                      {format(new Date(appointment.date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>{appointment.timeSlot}</TableCell>
                    <TableCell>
                      <Badge className={statusColorMap[appointment.status] || ''}>
                        {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8 px-2"
                          onClick={() => openDetailsDialog(appointment)}
                        >
                          Details
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="h-8 px-2"
                          onClick={() => openStatusDialog(appointment)}
                        >
                          Status
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
      
      {/* Appointment Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
          </DialogHeader>
          {currentAppointment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground">Date</h3>
                  <p>{format(new Date(currentAppointment.date), 'MMMM dd, yyyy')}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground">Time</h3>
                  <p>{currentAppointment.timeSlot}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground">Status</h3>
                  <Badge className={statusColorMap[currentAppointment.status] || ''}>
                    {currentAppointment.status.charAt(0).toUpperCase() + currentAppointment.status.slice(1)}
                  </Badge>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground">Doctor</h3>
                  <p>{getDoctorName(currentAppointment.doctorId)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold mb-2">Patient Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Name</h3>
                    <p>{currentAppointment.patientName}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Email</h3>
                    <p>{currentAppointment.patientEmail || 'Not provided'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Phone</h3>
                    <p>{currentAppointment.patientPhone || 'Not provided'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Gender</h3>
                    <p>{currentAppointment.patientGender || 'Not provided'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Age</h3>
                    <p>{currentAppointment.patientAge ? `${currentAppointment.patientAge} years` : 'Not provided'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Address</h3>
                    <p>{currentAppointment.patientAddress || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsDetailsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Status Update Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Appointment Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Update the status for {currentAppointment?.patientName}'s appointment on {currentAppointment?.date} at {currentAppointment?.timeSlot}.
            </p>
            <div className="flex flex-col gap-2">
              <Button 
                variant={currentAppointment?.status === 'confirmed' ? "default" : "outline"}
                onClick={() => handleStatusChange('confirmed')}
              >
                Confirm Appointment
              </Button>
              <Button 
                variant={currentAppointment?.status === 'completed' ? "default" : "outline"}
                onClick={() => handleStatusChange('completed')}
              >
                Mark as Completed
              </Button>
              <Button 
                variant={currentAppointment?.status === 'cancelled' ? "destructive" : "outline"}
                className={currentAppointment?.status !== 'cancelled' ? "text-red-500 hover:text-red-600" : ""}
                onClick={() => handleStatusChange('cancelled')}
              >
                Cancel Appointment
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentManagement;
